import { applyLandingMetadata } from './landing/landingSeo';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import MidLogo from '../components/MidLogo';

export default function PrivacyPage() {
  useEffect(() => applyLandingMetadata('/privacy', false), []);

  const lastUpdate = new Date().toLocaleDateString('pt-PT', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gray-50 public-legal">
      {/* Header */}
      <header className="bg-[var(--brand-paper,#fff)] border-b border-gray-100 sticky top-0 z-50 public-header">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <MidLogo variant="light" size="lg" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-[var(--brand-surface,#fff)] rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 public-card">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Política de Privacidade</h1>
              <p className="text-sm text-muted-foreground">Última atualização: {lastUpdate}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-muted-foreground mb-8">
              A proteção dos dados pessoais dos utilizadores é uma prioridade para a plataforma miD. Esta política explica como os dados são recolhidos e utilizados.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg my-6">
              <p className="font-medium mb-2">Responsável pelo tratamento de dados:</p>
              <p>Bruno Acácio Simões</p>
            </div>

            <h2 className="text-xl font-bold mt-8 mb-4">Dados Recolhidos</h2>
            <p>A plataforma pode recolher os seguintes tipos de dados pessoais:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Dados de identificação:</strong> nome, email, fotografia (a do teu perfil Google, se entrares com Google, ou a que carregares)</li>
              <li><strong>Dados de preferências:</strong> preferências de habitação, estilo de casa, orçamento</li>
              <li><strong>Dados financeiros:</strong> rendimento mensal, poupança disponível e encargos que indicas no Buyer DNA e no Mortgage Readiness</li>
              <li><strong>Dados de localização:</strong> localização aproximada, locais de trabalho</li>
              <li><strong>Dados de utilização:</strong> interações com a plataforma, histórico de análises</li>
              <li><strong>Dados técnicos:</strong> endereço IP, tipo de dispositivo, browser</li>
              <li><strong>Dados de análise (só com a tua autorização):</strong> páginas visitadas e gravações de sessão, recolhidas com o PostHog</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4">Finalidade do Tratamento</h2>
            <p>Os dados pessoais são utilizados para as seguintes finalidades:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Funcionamento e prestação dos serviços da plataforma</li>
              <li>Cálculo de compatibilidade entre compradores e imóveis</li>
              <li>Criação e gestão de perfis de utilizador</li>
              <li>Melhoria da experiência do utilizador</li>
              <li>Comunicação com utilizadores sobre o serviço</li>
              <li>Cumprimento de obrigações legais</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4">Base Legal para o Tratamento</h2>
            <p>O tratamento de dados pessoais baseia-se nas seguintes bases legais previstas no RGPD:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Execução do contrato:</strong> tratamento necessário para a prestação dos serviços solicitados</li>
              <li><strong>Consentimento do utilizador:</strong> quando o utilizador autoriza expressamente determinado tratamento</li>
              <li><strong>Interesse legítimo:</strong> para melhoria dos serviços e segurança da plataforma</li>
              <li><strong>Obrigação legal:</strong> quando exigido por lei</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4">Direitos dos Utilizadores</h2>
            <p>Nos termos do Regulamento Geral sobre a Proteção de Dados (RGPD), os utilizadores têm os seguintes direitos:</p>
            <div className="grid sm:grid-cols-2 gap-4 my-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Acesso</p>
                <p className="text-sm text-muted-foreground">Obter confirmação e acesso aos seus dados pessoais</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Retificação</p>
                <p className="text-sm text-muted-foreground">Corrigir dados inexatos ou incompletos</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Eliminação</p>
                <p className="text-sm text-muted-foreground">Solicitar a eliminação dos seus dados</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Portabilidade</p>
                <p className="text-sm text-muted-foreground">Receber os seus dados em formato estruturado</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Limitação</p>
                <p className="text-sm text-muted-foreground">Limitar o tratamento dos seus dados</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">Direito de Oposição</p>
                <p className="text-sm text-muted-foreground">Opor-se ao tratamento dos seus dados</p>
              </div>
            </div>
            <p>Para exercer qualquer destes direitos, contacte-nos através da plataforma.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Partilha de Dados</h2>
            <p>Os dados pessoais podem ser partilhados com:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Agentes imobiliários (AMI):</strong> qualquer agente registado no miD vê o teu primeiro nome e fotografia e um resumo do teu Buyer DNA (agregado, tipologia, orçamento, zonas, fase de preparação e arquétipo). O teu email, telefone e locais de trabalho exatos nunca são partilhados. Só falas com um agente depois de aceitares um match ou pedires uma visita e apenas por mensagens no miD.</li>
              <li><strong>Intermediários de crédito:</strong> quando pedes contacto a um intermediário de crédito, ele recebe o teu nome, email, o contacto que escolheste, o teu Buyer Readiness Score, o teu rendimento e a tua poupança, para te contactar e procurar soluções de crédito habitação.</li>
              <li><strong>PostHog (análise de utilização):</strong> só depois de aceitares os cookies analíticos. Os dados ficam em servidores do PostHog na União Europeia. Nas gravações de sessão, tudo o que escreves e a conversa do Buyer DNA ficam ocultos, e os teus dados financeiros nunca são gravados.</li>
              <li><strong>Prestadores de serviços:</strong> para processamento de pagamentos (Stripe) e outras funcionalidades técnicas</li>
              <li><strong>Autoridades:</strong> quando exigido por lei</li>
            </ul>
            <p className="mt-4">A plataforma não vende dados pessoais a terceiros.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Conservação de Dados</h2>
            <p>Os dados pessoais são conservados apenas pelo período necessário para:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>A prestação dos serviços solicitados</li>
              <li>O cumprimento de obrigações legais</li>
              <li>A defesa de interesses legítimos em caso de litígio</li>
            </ul>
            <p className="mt-4">Após este período, os dados são eliminados ou anonimizados.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Segurança dos Dados</h2>
            <p>A plataforma implementa medidas técnicas e organizativas adequadas para proteger os dados pessoais contra:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Acesso não autorizado</li>
              <li>Alteração indevida</li>
              <li>Divulgação não autorizada</li>
              <li>Destruição acidental ou ilícita</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4">Transferências Internacionais</h2>
            <p>Alguns prestadores de serviços podem estar localizados fora do Espaço Económico Europeu. Nesses casos, asseguramos que existem garantias adequadas de proteção de dados.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Alterações à Política</h2>
            <p>Esta política pode ser atualizada periodicamente. Os utilizadores serão informados de alterações relevantes.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Contacto e Reclamações</h2>
            <p>Para questões sobre proteção de dados, contacte-nos através da plataforma.</p>
            <p className="mt-4">Os utilizadores têm também o direito de apresentar reclamação junto da autoridade de controlo competente: <strong>Comissão Nacional de Proteção de Dados (CNPD)</strong>.</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[var(--brand-paper,#fff)] border-t border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} miD. Todos os direitos reservados.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-primary">Termos e Condições</Link>
            <Link to="/privacy" className="hover:text-primary">Política de Privacidade</Link>
            <Link to="/cookies" className="hover:text-primary">Política de Cookies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
